import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, ShieldCheck } from "@phosphor-icons/react";

/**
 * Public privacy statement (AVG / Art. 13-14 + transparency).
 * Not protected — anyone can read this without logging in.
 *
 * Disclaimer: deze tekst is opgesteld als MVP voor AVG-compliance en
 * dient nog door een jurist gereviewd te worden voordat hij definitief
 * wordt vastgesteld.
 */
export default function PrivacyStatementPage() {
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="privacy-back-home">
            <Briefcase size={22} weight="duotone" color="#1E88E5" />
            <span className="font-display text-lg font-bold tracking-tight">Werk Dat Past Online</span>
          </Link>
          <Link
            to="/login"
            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 transition-colors"
            data-testid="privacy-to-login"
          >
            Naar inloggen →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-statement">
        <div className="flex items-start gap-3 mb-6">
          <ShieldCheck size={28} weight="duotone" className="text-[#1E88E5] mt-1" />
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Privacyverklaring</h1>
            <p className="text-sm text-zinc-500 mt-1">Laatst bijgewerkt: 7 februari 2026</p>
          </div>
        </div>

        <Section title="1. Wie is de verwerkingsverantwoordelijke?">
          <p>
            <strong>TIOUW.com BV</strong> (hierna: <em>&ldquo;wij&rdquo;</em>) is de verwerkingsverantwoordelijke voor de
            persoonsgegevens die in de Werk Dat Past Online app worden verwerkt.
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Adres: Binderij 7-L, 1185 ZH Amstelveen, Nederland</li>
            <li>KvK-nummer: 05076640</li>
            <li>Contact privacy: via je work coach of via <a href="mailto:joost@tiouw.com" className="underline">joost@tiouw.com</a></li>
          </ul>
        </Section>

        <Section title="2. Welke persoonsgegevens verwerken wij?">
          <p>Afhankelijk van wat je in de app invult, kunnen wij de volgende categorieën gegevens verwerken:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Identificatiegegevens:</strong> naam, e-mailadres, gekoppeld bedrijf en toegewezen work coach.</li>
            <li><strong>CV-gegevens:</strong> adres, geboortedatum, foto (optioneel), werkervaring, opleiding, talenkennis, vaardigheden, interesses.</li>
            <li><strong>Testresultaten:</strong> uitkomsten en antwoorden van de breintype-test, IQ-test, werkwaarden-test, loopbaanwaarden-test, conflicthanteringstest, competentietest, beroepentest.</li>
            <li><strong>AI-gespreksverlopen:</strong> inhoud van chatgesprekken die je voert met de Sollicitatiegesprek-, Conflicthantering- en Beroepentest-chatbots.</li>
            <li><strong>Profielgegevens:</strong> velden die je invult in je zoek-, werkgevers- en werknemerprofiel, en het LinkedIn-stappenplan. Dit kan informatie bevatten over je beschikbaarheid, medische status (alleen als je die zelf invult), randvoorwaarden en thuissituatie.</li>
            <li><strong>360°-feedback:</strong> beoordelingen die door jou aangewezen reviewers via een anonieme link achterlaten op jouw competenties.</li>
            <li><strong>Technische gegevens:</strong> ingelogde sessies (JWT-tokens), tijdstempels van aanmelden en activiteit.</li>
          </ul>
          <p className="mt-2">Wij vragen of registreren géén bijzondere persoonsgegevens zoals etniciteit, religie, politieke voorkeur of seksuele geaardheid.</p>
        </Section>

        <Section title="3. Waarom verwerken wij deze gegevens?">
          <ul className="list-disc list-inside space-y-1">
            <li>Om je work coach te helpen jou te begeleiden bij je loopbaan of re-integratie.</li>
            <li>Om gepersonaliseerde testresultaten, profielen en AI-adviezen te produceren die zijn afgestemd op jouw situatie.</li>
            <li>Om de app beveiligd, betrouwbaar en functioneel te houden.</li>
          </ul>
        </Section>

        <Section title="4. Wat is de rechtsgrondslag?">
          <p>De verwerking gebeurt op basis van:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Uitvoering van een overeenkomst</strong> (AVG art. 6 lid 1 sub b): jouw coaching- of re-integratietraject, waarvan deze app onderdeel uitmaakt.</li>
            <li><strong>Toestemming</strong> (art. 6 lid 1 sub a): voor het opslaan van testresultaten en AI-gespreksverlopen. Je geeft deze toestemming bij eerste login en kunt deze op elk moment intrekken via &ldquo;Mijn privacy&rdquo; of door je gegevens te laten verwijderen.</li>
            <li><strong>Gerechtvaardigd belang</strong> (art. 6 lid 1 sub f): voor het beveiligen en onderhouden van de dienst.</li>
          </ul>
        </Section>

        <Section title="5. Met wie delen wij jouw gegevens?">
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Je toegewezen work coach en het work coach bedrijf:</strong> hebben toegang tot alle gegevens die jij in de app invult, behalve je wachtwoord.</li>
            <li><strong>Beheerder van het platform</strong> (TIOUW.com BV): heeft technische toegang voor onderhoud, alleen op need-to-know-basis.</li>
            <li><strong>AI-verwerker — Anthropic Claude</strong> via de &ldquo;Emergent LLM Key&rdquo; infrastructuur: een deel van je gegevens (CV-uittreksel, testantwoorden) wordt verstuurd naar Claude om gepersonaliseerde adviezen, rapporten en chatantwoorden te genereren. Anthropic verwerkt deze gegevens als sub-verwerker en bewaart deze niet voor training (per hun standaard enterprise-voorwaarden).</li>
            <li><strong>Reviewers voor 360°-feedback:</strong> alleen wanneer jij ze zelf de link stuurt; zij zien alleen je voornaam en de namen + omschrijvingen van je competenties.</li>
            <li>Wij verkopen of verhuren je gegevens niet aan derden. Wij gebruiken ze niet voor reclame of profilering buiten de app.</li>
          </ul>
        </Section>

        <Section title="6. Worden gegevens buiten de EU verwerkt?">
          <p>
            Anthropic verwerkt mogelijk gegevens via servers in de Verenigde Staten. Dit gebeurt onder
            passende waarborgen (Standard Contractual Clauses van de Europese Commissie). De rest van
            de infrastructuur staat in een Europees datacenter.
          </p>
        </Section>

        <Section title="7. Hoe lang bewaren wij jouw gegevens?">
          <p>
            Wij bewaren je gegevens zo lang als nodig is voor het coachings- of re-integratietraject
            en daarna nog maximaal <strong>vijf jaar</strong> ten behoeve van verantwoording (zoals
            vereist door UWV of opdrachtgever). Daarna worden je gegevens automatisch verwijderd.
            Je kunt op elk moment vragen om eerdere verwijdering.
          </p>
        </Section>

        <Section title="8. Welke rechten heb je?">
          <p>Onder de AVG heb je de volgende rechten:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Recht op inzage</strong> (art. 15): je kunt een volledige kopie van je gegevens downloaden via &ldquo;Mijn privacy&rdquo; → &ldquo;Download al mijn gegevens&rdquo;.</li>
            <li><strong>Recht op rectificatie</strong> (art. 16): je kunt je CV en profielvelden zelf aanpassen in de app.</li>
            <li><strong>Recht op vergetelheid</strong> (art. 17): je kunt via &ldquo;Mijn privacy&rdquo; een verwijderingsverzoek indienen. Je work coach of een administrator beoordeelt en bevestigt dit binnen 30 dagen.</li>
            <li><strong>Recht op beperking</strong> (art. 18): vraag je work coach of TIOUW.com BV om de verwerking te pauzeren.</li>
            <li><strong>Recht op dataportabiliteit</strong> (art. 20): de download bij &ldquo;Mijn privacy&rdquo; is een gestructureerd JSON-bestand dat overdraagbaar is.</li>
            <li><strong>Recht van bezwaar</strong> (art. 21): je kunt bezwaar maken tegen verwerking op basis van gerechtvaardigd belang.</li>
            <li><strong>Recht om toestemming in te trekken</strong>: dit kan op elk moment, zonder gevolgen voor verwerking die al heeft plaatsgevonden.</li>
            <li><strong>Recht om een klacht in te dienen</strong> bij de <a href="https://autoriteitpersoonsgegevens.nl/" className="underline" rel="noopener noreferrer" target="_blank">Autoriteit Persoonsgegevens</a>.</li>
          </ul>
        </Section>

        <Section title="9. Hoe beveiligen wij jouw gegevens?">
          <ul className="list-disc list-inside space-y-1">
            <li>Wachtwoorden worden versleuteld opgeslagen (bcrypt).</li>
            <li>Alle communicatie verloopt via HTTPS.</li>
            <li>Sessies werken met JWT-tokens die automatisch verlopen.</li>
            <li>Toegang voor work coaches en administrators is strikt gescheiden per bedrijf.</li>
            <li>Wij houden audit-logs van wijzigingen op gevoelige acties.</li>
          </ul>
        </Section>

        <Section title="10. Cookies">
          <p>
            De app gebruikt uitsluitend <strong>functionele cookies</strong> die nodig zijn om
            ingelogd te blijven. Wij gebruiken geen tracking-cookies, geen advertentienetwerken
            en geen externe analyse-tools.
          </p>
        </Section>

        <Section title="11. Wijzigingen op deze privacyverklaring">
          <p>
            Als wij deze verklaring inhoudelijk wijzigen, vragen wij je opnieuw om akkoord bij de
            eerstvolgende login. Eerdere versies zijn op verzoek beschikbaar.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Vragen over deze verklaring of over de verwerking van jouw gegevens? Neem contact op
            met je work coach of e-mail <a href="mailto:joost@tiouw.com" className="underline">joost@tiouw.com</a>.
          </p>
        </Section>
      </main>

      <footer className="border-t border-zinc-200 mt-10">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-zinc-500">
          © 2026 TIOUW.com BV — Binderij 7-L, 1185 ZH Amstelveen, Nederland — KvK 05076640
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8" data-testid={`privacy-section-${slugify(title)}`}>
      <h2 className="font-display text-xl font-bold tracking-tight mb-2">{title}</h2>
      <div className="text-sm text-zinc-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
